/**
 * Converts a path string with dynamic segments (e.g., `:id`) into a regular expression
 * that matches partial paths containing the same structure.
 *
 * This function replaces segments in the path that are prefixed with `:` (e.g., `:id`)
 * with a regular expression that matches any string except a forward slash (`/`).
 *
 * The resulting regular expression will match partial paths, without needing the entire
 * path to match (i.e., no start `^` or end `$` anchors are enforced).
 *
 * @param {string} path - The path string containing dynamic segments prefixed with `:`.
 *                        For example, `/settings/warehouse/direct-sale-variants/:id`.
 *
 * @returns {RegExp} A regular expression that matches the given path structure
 *                   with dynamic segments replaced by matching patterns.
 *
 * @example
 * const regex = pathToPartialRegex("/settings/warehouse/direct-sale-variants/:id");
 * console.log(regex);
 * // Output: /\/settings\/warehouse\/direct-sale-variants\/([^/]+)/i
 *
 * console.log(regex.test("/settings/warehouse/direct-sale-variants/123")); // true
 * console.log(regex.test("/prefix/settings/warehouse/direct-sale-variants/abc")); // true
 * console.log(regex.test("/other/unrelated/path")); // false
 */
export const pathToPartialRegex = (path: string): RegExp => {
  // Escape special regex characters
  const escapedPath = path.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');

  // Replace :param with a regex that matches any string except a forward slash
  const regexString = escapedPath.replace(':id', '([^\\/]+)');

  // Return as a RegExp object without the start (^) or end ($) anchors
  // eslint-disable-next-line security/detect-non-literal-regexp
  return new RegExp(regexString, 'i');
};
